/**
 * Product Finder Quiz — coordinates separate sections on the same page.
 * Each step section uses data-bst-product-finder-step.
 */
(function () {
  const STEP_ORDER = ['hero', 'kitchen', 'cooking', 'priorities', 'results'];
  const LEGACY_STORAGE_KEY = 'bst-product-finder-answers';

  function parseMatchTags(value) {
    if (!value) return [];
    return value
      .split(',')
      .map((tag) => tag.trim())
      .filter(Boolean);
  }

  function scoreResultItem(item, answers) {
    const kitchenTags = parseMatchTags(item.dataset.bstProductFinderKitchen);
    const cookingTags = parseMatchTags(item.dataset.bstProductFinderCooking);
    const priorityTags = parseMatchTags(item.dataset.bstProductFinderPriorities);
    const hasCriteria = kitchenTags.length || cookingTags.length || priorityTags.length;

    if (!hasCriteria) return 0;

    let score = 0;

    if (kitchenTags.length) {
      if (!answers.kitchen_type || !kitchenTags.includes(answers.kitchen_type)) {
        return -1;
      }
      score++;
    }

    if (cookingTags.length) {
      if (!answers.cooking_style || !cookingTags.includes(answers.cooking_style)) {
        return -1;
      }
      score++;
    }

    if (priorityTags.length) {
      const userPriorities = Array.isArray(answers.priorities) ? answers.priorities : [];
      if (!priorityTags.some((tag) => userPriorities.includes(tag))) {
        return -1;
      }
      score++;
    }

    return score;
  }

  class BstProductFinderQuiz {
    constructor(root, steps, liveRegion) {
      this.root = root;
      this.steps = steps;
      this.liveRegion = liveRegion;
      this.answers = {};
      this.currentStep = 'hero';

      this.root.classList.add('bst-product-finder--is-wizard');
      this.bindEvents();
      this.showStep(this.currentStep, false);
    }

    bindEvents() {
      this.root.addEventListener('click', (event) => {
        const startBtn = event.target.closest('[data-bst-product-finder-start]');
        if (startBtn) {
          event.preventDefault();
          this.goTo('kitchen');
          return;
        }

        const backBtn = event.target.closest('[data-bst-product-finder-back]');
        if (backBtn) {
          event.preventDefault();
          const target = backBtn.getAttribute('data-bst-product-finder-back');
          if (target) this.goTo(target);
          return;
        }

        const continueBtn = event.target.closest('[data-bst-product-finder-continue]');
        if (continueBtn) {
          event.preventDefault();
          if (continueBtn.disabled || continueBtn.getAttribute('aria-disabled') === 'true') {
            return;
          }
          const target = continueBtn.getAttribute('data-bst-product-finder-continue');
          if (target) this.goTo(target);
          return;
        }

        const retakeBtn = event.target.closest('[data-bst-product-finder-retake]');
        if (retakeBtn) {
          event.preventDefault();
          this.answers = {};
          this.clearSelections();
          this.goTo('hero');
        }
      });

      this.root.addEventListener('change', (event) => {
        const input = event.target;
        if (!(input instanceof HTMLInputElement)) return;
        if (!input.matches('[data-bst-product-finder-input]')) return;

        const stepEl = input.closest('[data-bst-product-finder-step]');
        if (!stepEl) return;

        const stepId = stepEl.getAttribute('data-bst-product-finder-step');
        const field = input.getAttribute('name');

        if (input.type === 'radio') {
          this.answers[field] = input.value;
          this.updateOptionStates(stepEl);
        } else if (input.type === 'checkbox') {
          if (!Array.isArray(this.answers[field])) {
            this.answers[field] = [];
          }
          if (input.checked) {
            if (!this.answers[field].includes(input.value)) {
              this.answers[field].push(input.value);
            }
          } else {
            this.answers[field] = this.answers[field].filter((v) => v !== input.value);
          }
          this.updateOptionStates(stepEl);
        }

        this.updateContinueState(stepEl);
      });

      this.root.addEventListener('keydown', (event) => {
        const label = event.target.closest('.bst-product-finder-quiz__option-label');
        if (!label) return;
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          const input = label.querySelector('[data-bst-product-finder-input]');
          if (input) {
            if (input.type === 'checkbox') {
              input.checked = !input.checked;
            } else {
              input.checked = true;
            }
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
      });
    }

    updateOptionStates(stepEl) {
      const options = stepEl.querySelectorAll('.bst-product-finder-quiz__option');
      options.forEach((option) => {
        const input = option.querySelector('[data-bst-product-finder-input]');
        option.classList.toggle(
          'bst-product-finder-quiz__option--selected',
          input && input.checked
        );
      });
    }

    updateContinueState(stepEl) {
      const continueBtn = stepEl.querySelector('[data-bst-product-finder-continue]');
      if (!continueBtn) return;

      const inputs = stepEl.querySelectorAll('[data-bst-product-finder-input]');
      if (!inputs.length) return;

      const isCheckbox = inputs[0].type === 'checkbox';
      let enabled = false;

      if (isCheckbox) {
        enabled = Array.from(inputs).some((input) => input.checked);
      } else {
        enabled = Array.from(inputs).some((input) => input.checked);
      }

      continueBtn.disabled = !enabled;
      continueBtn.setAttribute('aria-disabled', enabled ? 'false' : 'true');
    }

    clearSelections() {
      this.steps.forEach((stepEl) => {
        const inputs = stepEl.querySelectorAll('[data-bst-product-finder-input]');
        inputs.forEach((input) => {
          input.checked = false;
        });
        this.updateOptionStates(stepEl);
        this.updateContinueState(stepEl);
      });
    }

    clearStepsAfter(stepId) {
      const targetIndex = STEP_ORDER.indexOf(stepId);
      if (targetIndex === -1) return;

      for (let i = targetIndex + 1; i < STEP_ORDER.length; i++) {
        const stepEl = this.steps.find(
          (el) => el.getAttribute('data-bst-product-finder-step') === STEP_ORDER[i]
        );
        if (!stepEl) continue;

        const inputs = stepEl.querySelectorAll('[data-bst-product-finder-input]');
        inputs.forEach((input) => {
          const field = input.getAttribute('name');
          if (field) delete this.answers[field];
          input.checked = false;
        });
        this.updateOptionStates(stepEl);
        this.updateContinueState(stepEl);
      }
    }

    restoreSelections() {
      this.steps.forEach((stepEl) => {
        const inputs = stepEl.querySelectorAll('[data-bst-product-finder-input]');
        inputs.forEach((input) => {
          const field = input.getAttribute('name');
          if (input.type === 'radio') {
            input.checked = this.answers[field] === input.value;
          } else if (input.type === 'checkbox') {
            input.checked =
              Array.isArray(this.answers[field]) &&
              this.answers[field].includes(input.value);
          }
        });
        this.updateOptionStates(stepEl);
        this.updateContinueState(stepEl);
      });
    }

    goTo(stepId) {
      const index = STEP_ORDER.indexOf(stepId);
      if (index === -1) return;

      const currentIndex = STEP_ORDER.indexOf(this.currentStep);
      if (index < currentIndex) {
        this.clearStepsAfter(stepId);
      }

      this.showStep(stepId, true);
    }

    showStep(stepId, announce) {
      this.currentStep = stepId;

      if (stepId === 'results') {
        this.updateResults();
      }

      this.steps.forEach((stepEl) => {
        const id = stepEl.getAttribute('data-bst-product-finder-step');
        const isActive = id === stepId;
        stepEl.hidden = !isActive;
        stepEl.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      });

      this.restoreSelections();

      const activeStep = this.steps.find(
        (el) => el.getAttribute('data-bst-product-finder-step') === stepId
      );

      if (activeStep) {
        const focusTarget =
          activeStep.querySelector('[data-bst-product-finder-focus]') ||
          activeStep.querySelector('h1, h2, button, a');
        if (focusTarget && announce) {
          focusTarget.focus({ preventScroll: false });
        }
      }

      if (announce && this.liveRegion) {
        const label = activeStep?.getAttribute('data-bst-product-finder-step-label');
        if (label) {
          this.liveRegion.textContent = label;
        }
      }

      const url = new URL(window.location.href);
      if (stepId === 'hero') {
        url.searchParams.delete('step');
      } else {
        url.searchParams.set('step', stepId);
      }
      window.history.replaceState({}, '', url);
    }

    updateResults() {
      const resultsStep = this.steps.find(
        (el) => el.getAttribute('data-bst-product-finder-step') === 'results'
      );
      if (!resultsStep) return;

      const grid = resultsStep.querySelector('[data-bst-product-finder-results-grid]');
      const emptyMessage = resultsStep.querySelector('[data-bst-product-finder-results-empty]');
      if (!grid) return;

      const items = Array.from(
        grid.querySelectorAll('[data-bst-product-finder-results-item]')
      );
      const ranked = items
        .map((item) => ({ item, score: scoreResultItem(item, this.answers) }))
        .filter((entry) => entry.score > 0)
        .sort((a, b) => b.score - a.score);

      const visibleItems =
        ranked.length > 0
          ? ranked.map((entry) => entry.item)
          : items.filter((item) => scoreResultItem(item, this.answers) === 0);

      items.forEach((item) => {
        const isVisible = visibleItems.includes(item);
        item.hidden = !isVisible;
      });

      if (emptyMessage) {
        emptyMessage.hidden = visibleItems.length > 0;
      }

      grid.hidden = visibleItems.length === 0;
    }
  }

  function clearStoredQuizData() {
    try {
      sessionStorage.removeItem(LEGACY_STORAGE_KEY);
      localStorage.removeItem(LEGACY_STORAGE_KEY);
    } catch {
      /* ignore storage access errors */
    }
  }

  function init() {
    if (document.documentElement.dataset.bstProductFinderInit === 'true') return;

    const steps = Array.from(
      document.querySelectorAll('[data-bst-product-finder-step]')
    );

    document.documentElement.classList.remove('bst-product-finder-pending');

    if (steps.length < 2) return;

    clearStoredQuizData();

    const url = new URL(window.location.href);
    if (url.searchParams.has('step')) {
      url.searchParams.delete('step');
      window.history.replaceState({}, '', url);
    }

    document.documentElement.dataset.bstProductFinderInit = 'true';

    let liveRegion = document.querySelector('[data-bst-product-finder-live]');
    if (!liveRegion) {
      liveRegion = document.createElement('div');
      liveRegion.setAttribute('data-bst-product-finder-live', '');
      liveRegion.className = 'bst-product-finder__sr-live';
      liveRegion.setAttribute('aria-live', 'polite');
      liveRegion.setAttribute('aria-atomic', 'true');
      document.body.appendChild(liveRegion);
    }

    steps.forEach((step) => {
      step.classList.add('bst-product-finder--wizard');
    });

    new BstProductFinderQuiz(document.documentElement, steps, liveRegion);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
